import { useTradingIntent } from "features/onboarding/guided/intent";
import { matchPath, useLocation, useRouteMatch } from "react-router-dom";
import React, { useContext } from "react";
import { PageWrapperContext } from "common/Home/PageWrapper/PageWrapper.context.tsx";
import {
  CREATE_TRADE_PATH,
  MARKETPLACE_PATH,
  MCP_PATH,
  FINANCE_PATH,
  COLLAPSED_DESKTOP_MENU,
  PORTFOLIO_ACCOUNTS_PATH,
  PORTFOLIO_ASSETS_PATH,
  WATCHLIST_PATH,
  SECURITY_SETTINGS_PATH,
  BILLING_SETTINGS_PATH,
  DASHBOARD_PATH,
  GROUP_MANAGEMENT_PATH,
  BASE_BOTS_SIGNALS_PATH,
  AFFILIATION_PATH,
  BASE_BOTS_PATH,
  ADMIN_TRADING_CONFIGURATION_FORM_PATH,
  MOBILE_SEARCH,
  NOTIFICATIONS_SETTINGS_PATH,
  MY_GROUPS_PATH,
  ADMIN_SIGNALS_PATH,
  USERS_MANAGEMENT_PATH,
  ADMIN_TRADING_CONFIGURATION_PATH,
  TRADES_PATH,
  PORTFOLIO_PATH,
  CREATE_SIGNAL_PATH,
  USER_SIGNALS_PATH,
  CONNECT_WHOP_CHANNEL_PATH,
  INVOICES_PATH,
  BUSINESS_PROFILE_PATH,
  MOBILE_MENU,
} from "common/constants.ts";
import styled, { useTheme } from "styled-components";
import { typography } from "@shared/common/Themes/typography.ts";
import { ONBOARDING_PATHS } from "common/constants.ts";
import { PATH_TO_DETAILED_STEP } from "features/onboarding/common/constants.ts";
import { ONBOARDING_STEP_TO_TITLE } from "@shared/common/constants.ts";
import useMedia from "utils/hooks/useMedia.ts";
import { useBottomNavigator } from "utils/hooks/misc.tsx";
import { selectWorkspace } from "@shared/slices/common.slice.ts";
import { OnboardingDetailedStep } from "@shared/features/onboarding/types.ts";
import { LanguageNameSpaces, WorkspaceType } from "@shared/types";
import { useSelector } from "react-redux";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import AutoSizeText from "common/Text/AutoSizeText.tsx";
import useBotsPageTitleString from "common/Home/General/useBotsPageTitleString.tsx";
import useCreateTradeTitleString from "common/Home/General/useCreateTradeTitleString.tsx";
import { phoneMaxWidth } from "common/NewLook/layout.ts";
import { PAGE_TITLE_ID } from "testIds";

const PageTitle = () => {
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const isMobileCreateTrade = useMedia(
    [`(max-width: ${MOBILE_MENU})`],
    [true],
    false
  );
  const routeTitle = usePageTitleString();
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const isCreateTrade = !!useRouteMatch([
    CREATE_TRADE_PATH,
    CREATE_SIGNAL_PATH,
  ]);
  const { titleExtraComponent } = useContext(PageWrapperContext);
  const appTheme = useTheme();
  const { isNewLook } = appTheme;
  const { pathname } = useLocation();
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  // The new look lists accounts on their own; the view reads better as "Accounts".
  const isNewLookAccounts =
    isNewLook &&
    !isBottomNavigator &&
    NEW_LOOK_ACCOUNTS_ROUTES.some((path) =>
      matchPath(pathname, { path, exact: true })
    );
  const title = isNewLookAccounts ? t("page_title.accounts") : routeTitle;

  const content = isBottomNavigator ? (
    <AutoSizeText
      baseFontSizePx={isNewLook ? 16 : undefined}
      fontWeight={
        isNewLook ? appTheme.typography.fontWeights.MEDIUM : undefined
      }
      style={isNewLook ? { color: appTheme.colors.titleColor } : undefined}
    >
      {title}
    </AutoSizeText>
  ) : (
    title
  );

  return (
    <Title $isMobile={isMobile} data-testid={PAGE_TITLE_ID}>
      {content}
      {isCreateTrade && !isMobileCreateTrade && titleExtraComponent}
    </Title>
  );
};

export default PageTitle;

export const usePageTitleString = (): string => {
  const pathname = useLocation().pathname;
  const { intent } = useTradingIntent();
  const { t: translateOnboarding } = useTypedTranslation(
    LanguageNameSpaces.ONBOARDING
  );
  const bottomNavigatorData = useBottomNavigator();
  const workspace = useSelector(selectWorkspace);
  const isDemoWorkspace = workspace === WorkspaceType.DEMO;
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);

  const botsTitle = useBotsPageTitleString();
  const createTradeTitle = useCreateTradeTitleString();

  const isDashboard = !!useRouteMatch({ path: DASHBOARD_PATH, exact: false });
  const isGroupManagement = !!useRouteMatch(GROUP_MANAGEMENT_PATH);
  const isUsersManagement = !!useRouteMatch(USERS_MANAGEMENT_PATH);
  const isSignals = !!useRouteMatch([
    ADMIN_SIGNALS_PATH,
    BASE_BOTS_SIGNALS_PATH,
    USER_SIGNALS_PATH,
  ]);
  const isPortfolioAssets = !!useRouteMatch(PORTFOLIO_ASSETS_PATH);
  const isPortfolioAccounts = !!useRouteMatch(PORTFOLIO_ACCOUNTS_PATH);
  const isPortfolio = !!useRouteMatch(PORTFOLIO_PATH);
  const isMyGroups = !!useRouteMatch(MY_GROUPS_PATH);
  const isCreateTrade = !!useRouteMatch([
    CREATE_TRADE_PATH,
    CREATE_SIGNAL_PATH,
  ]);
  const isTrades = !!useRouteMatch(TRADES_PATH);
  const isBots = !!useRouteMatch([
    BASE_BOTS_PATH,
    ADMIN_TRADING_CONFIGURATION_FORM_PATH,
  ]);
  const isWatchlist = !!useRouteMatch(WATCHLIST_PATH);
  const isMarketplace = !!useRouteMatch(MARKETPLACE_PATH);
  const isMcp = !!useRouteMatch(MCP_PATH);
  const isFinance = !!useRouteMatch(FINANCE_PATH);
  const isSecurity = !!useRouteMatch(SECURITY_SETTINGS_PATH);
  const isNotifications = !!useRouteMatch(NOTIFICATIONS_SETTINGS_PATH);
  const isBilling = !!useRouteMatch(BILLING_SETTINGS_PATH);
  const isAffiliation = !!useRouteMatch(AFFILIATION_PATH);
  const isGroupConfigurations = !!useRouteMatch(
    ADMIN_TRADING_CONFIGURATION_PATH
  );
  const isConnectWhop = !!useRouteMatch(CONNECT_WHOP_CHANNEL_PATH);
  const isInvoices = !!useRouteMatch(INVOICES_PATH);
  const isAssetManager = !!useRouteMatch(BUSINESS_PROFILE_PATH);
  const isOnboarding = !!useRouteMatch(ONBOARDING_PATHS);

  if (isDashboard) return t("page_title.dashboard");
  if (isGroupManagement || isUsersManagement)
    return t("page_title.users_management");
  if (isSignals) return t("page_title.bots.signals");
  if (bottomNavigatorData) {
    if (isPortfolioAssets || isPortfolioAccounts || isPortfolio)
      return t("page_title.portfolio");
  } else {
    if (isPortfolioAssets) return t("page_title.assets");
    if (isPortfolioAccounts) return t("page_title.accounts");
  }
  if (isMyGroups) return t("page_title.my_groups");
  if (isCreateTrade) return createTradeTitle;
  if (isTrades) return t("page_title.trades");
  if (isBots) return botsTitle;
  if (isWatchlist) return t("page_title.watchlist_and_markets");
  if (isMarketplace) return t("page_title.marketplace");
  if (isMcp) return t("page_title.mcp");
  if (isFinance) return t("page_title.finance");
  if (isSecurity) return t("page_title.account_and_security");
  if (isNotifications) return t("page_title.notifications_management");
  if (isBilling) return t("page_title.billing");
  if (isAffiliation) return t("page_title.affiliation");
  if (isGroupConfigurations) return t("page_title.group_configurations");
  if (isConnectWhop) return t("page_title.connect_group_to_cornix");
  if (isInvoices) return t("page_title.invoices");
  if (isAssetManager) return t("page_title.business_profile");
  if (isOnboarding) {
    const step = PATH_TO_DETAILED_STEP[pathname];
    if (step === OnboardingDetailedStep.SELECT_EXCHANGE && !intent)
      return t("page_title.onboarding.trading_method");
    if (intent)
      return translateOnboarding(
        step === OnboardingDetailedStep.CONNECT_EXCHANGE && isDemoWorkspace
          ? "guided.step_account"
          : `intent.${intent}.name`
      );
    if (step === OnboardingDetailedStep.CONNECT_EXCHANGE && isDemoWorkspace)
      return t("page_title.onboarding.connect_demo_account");
    return t(ONBOARDING_STEP_TO_TITLE[step]);
  }

  return "";
};

const TITLE_MARGIN = 12;
const NEW_LOOK_ACCOUNTS_ROUTES = [PORTFOLIO_ACCOUNTS_PATH, PORTFOLIO_PATH];

const Title = styled.div<{ $isMobile: boolean }>`
  display: flex;
  align-content: center;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  font-weight: ${typography.fontWeights.BOLD};
  font-size: ${(p) => p.theme.typography.fontSizes.XL};
  @media (max-width: ${MOBILE_SEARCH}) {
    font-size: ${(p) => p.theme.typography.fontSizes.L};
  }
  @media (max-width: 420px) {
    font-size: 15px;
  }
  @media (max-width: 370px) {
    font-size: ${(p) => p.theme.typography.fontSizes.M};
  }
  font-size: ${(p) =>
    p.$isMobile
      ? p.theme.typography.fontSizes.L
      : p.theme.typography.fontSizes.XL};
  ${(p) =>
    p.theme.isNewLook &&
    !p.$isMobile &&
    `
    align-items: center;
    gap: 12px;
    font-size: 18px;
    font-weight: ${p.theme.typography.fontWeights.SEMI_BOLD};
    letter-spacing: -0.01em;
  `}
  flex: 1;
  height: 100%;
  color: ${(p) => p.theme.colors.titleColor};
  @media (max-width: ${COLLAPSED_DESKTOP_MENU}) {
    width: calc(100% - ${TITLE_MARGIN}px);
    margin-left: ${TITLE_MARGIN}px;
  }
  @media (max-width: ${phoneMaxWidth}) {
    width: 100%;
    margin-left: 0;
    font-weight: ${(p) =>
      p.theme.isNewLook
        ? p.theme.typography.fontWeights.SEMI_BOLD
        : typography.fontWeights.MEDIUM};
    text-align: center;
  }
`;
